import { api } from "../../api"
import { Board } from "../entities/board"
import { makeBoardApiEndpoints } from "./endpoints"
import { boardApiV2, selectBoardV2ById } from "./v2"
import { BoardResponseV2 } from "./v2/board-response"
import { BoardViewModelV2, mapBoardResponseToViewModel, mapBoardToViewModel } from "./v2/board-view-model"

const apiEndpoints = makeBoardApiEndpoints()
const lastUpdateRequestById: Record<string, string> = {}
const isLastUpdateRequest = (boardId: string, requestId: string) => lastUpdateRequestById[boardId] === requestId
const generateId = () => `temp-${Date.now()}${Math.random()}`

export const boardApi = api.injectEndpoints({
  endpoints: (builder) => ({
    addTaskCard: builder.mutation<BoardViewModelV2, NewCardData>({
      query: ({ boardId, columnId, ...newCardData }) => ({
        url: apiEndpoints.boardTaskCards(columnId, boardId),
        method: "POST",
        body: { ...newCardData, type: "task" },
      }),
      transformResponse: (response: BoardResponseV2) => mapBoardResponseToViewModel(response),
      invalidatesTags: (result) => ["Task"],
      onQueryStarted: async ({ boardId, ...newCardData }, { getState, dispatch, queryFulfilled, requestId }) => {
        lastUpdateRequestById[boardId] = requestId
        const state = getState()
        const localBoardData = selectBoardV2ById(state, boardId)

        if (localBoardData) {
          const boardQuery = { id: boardId, type: localBoardData.type }
          const localBoard = new Board(localBoardData)
          const toColumn = localBoard.getColumnById(newCardData.columnId)
          const columnPosition = toColumn?.position || 0
          const cardTempId = generateId()
          localBoard.addOptimisticCard({ ...newCardData, id: cardTempId, type: "task", columnPosition })
          const patchResult = dispatch(
            boardApiV2.util.updateQueryData("getBoardV2", boardQuery, (draft) => {
              Object.assign(draft, mapBoardToViewModel(localBoard))
            })
          )
          try {
            const response = await queryFulfilled
            const serverBoard = new Board(response.data)
            const serverCard = serverBoard.getColumnById(newCardData.columnId)?.getCardById(cardTempId)
            if (serverCard) {
              serverBoard.removeCard(cardTempId)
              serverBoard.addCard(serverCard, columnPosition)
            }

            // Only update the cache if this is the last move request for the board
            if (isLastUpdateRequest(boardId, requestId)) {
              dispatch(
                boardApiV2.util.updateQueryData("getBoardV2", boardQuery, (draft) => {
                  Object.assign(draft, mapBoardToViewModel(serverBoard))
                })
              )
            }
          } catch (error) {
            patchResult.undo()
          }
        }
      },
    }),
    addTodoCard: builder.mutation<BoardViewModelV2, NewCardData>({
      query: ({ boardId, columnId, ...newCardData }) => ({
        url: apiEndpoints.boardTodoCards(columnId, boardId),
        method: "POST",
        body: { ...newCardData, type: "todo" },
      }),
      transformResponse: (response: BoardResponseV2) => mapBoardResponseToViewModel(response),
      invalidatesTags: () => ["Todo"],
      onQueryStarted: async ({ boardId, ...newCardData }, { getState, dispatch, queryFulfilled, requestId }) => {
        lastUpdateRequestById[boardId] = requestId
        const state = getState()
        const localBoardData = selectBoardV2ById(state, boardId)

        if (localBoardData) {
          const boardQuery = { id: boardId, type: localBoardData.type }
          const localBoard = new Board(localBoardData)
          const toColumn = localBoard.getColumnById(newCardData.columnId)
          const columnPosition = toColumn?.position || 0
          const cardTempId = generateId()
          localBoard.addOptimisticCard({ ...newCardData, id: cardTempId, type: "todo", columnPosition })
          const patchResult = dispatch(
            boardApiV2.util.updateQueryData("getBoardV2", boardQuery, (draft) => {
              Object.assign(draft, mapBoardToViewModel(localBoard))
            })
          )
          try {
            const response = await queryFulfilled
            const serverBoard = new Board(response.data)
            const serverCard = serverBoard.getColumnById(newCardData.columnId)?.getCardById(cardTempId)
            if (serverCard) {
              serverBoard.removeCard(cardTempId)
              serverBoard.addCard(serverCard, columnPosition)
            }

            // Only update the cache if this is the last move request for the board
            if (isLastUpdateRequest(boardId, requestId)) {
              dispatch(
                boardApiV2.util.updateQueryData("getBoardV2", boardQuery, (draft) => {
                  Object.assign(draft, mapBoardToViewModel(serverBoard))
                })
              )
            }
          } catch (error) {
            patchResult.undo()
          }
        }
      },
    }),
    moveTodoCard: builder.mutation<BoardViewModelV2, MoveCardData>({
      query: ({ boardId, cardId, columnId, ...moveCardData }) => ({
        url: apiEndpoints.boardTodoCardItem(cardId, columnId, boardId),
        method: "PUT",
        body: moveCardData,
      }),
      transformResponse: (response: BoardResponseV2) => {
        return mapBoardResponseToViewModel(response)
      },
      invalidatesTags: () => ["Todo"],
      onQueryStarted: async ({ boardId, ...moveCardData }, { getState, dispatch, queryFulfilled, requestId }) => {
        lastUpdateRequestById[boardId] = requestId
        const state = getState()
        const localBoardData = selectBoardV2ById(state, boardId)

        if (localBoardData) {
          const boardQuery = { id: boardId, type: localBoardData.type }
          const localBoard = new Board(localBoardData)
          localBoard.moveCard(
            moveCardData.cardId,
            moveCardData.columnId,
            moveCardData.destColumnId,
            moveCardData.position
          )
          const patchResult = dispatch(
            boardApiV2.util.updateQueryData("getBoardV2", boardQuery, (draft) => {
              Object.assign(draft, mapBoardToViewModel(localBoard))
            })
          )
          try {
            const response = await queryFulfilled
            const serverBoard = response.data

            // Only update the cache if this is the last move request for the board
            if (isLastUpdateRequest(boardId, requestId)) {
              dispatch(
                boardApiV2.util.updateQueryData("getBoardV2", boardQuery, (draft) => {
                  Object.assign(draft, serverBoard)
                })
              )
            }
          } catch (error) {
            patchResult.undo()
          }
        }
      },
    }),
    moveTaskCard: builder.mutation<BoardViewModelV2, MoveCardData>({
      query: ({ boardId, cardId, columnId, ...moveCardData }) => ({
        url: apiEndpoints.boardTaskCardItem(cardId, columnId, boardId),
        method: "PUT",
        body: moveCardData,
      }),
      transformResponse: (response: BoardResponseV2) => {
        return mapBoardResponseToViewModel(response)
      },
      invalidatesTags: () => ["Task"],
      onQueryStarted: async ({ boardId, ...moveCardData }, { getState, dispatch, queryFulfilled, requestId }) => {
        lastUpdateRequestById[boardId] = requestId
        const state = getState()
        const localBoardData = selectBoardV2ById(state, boardId)

        if (localBoardData) {
          const boardQuery = { id: boardId, type: localBoardData.type }
          const localBoard = new Board(localBoardData)
          localBoard.moveCard(
            moveCardData.cardId,
            moveCardData.columnId,
            moveCardData.destColumnId,
            moveCardData.position
          )
          const patchResult = dispatch(
            boardApiV2.util.updateQueryData("getBoardV2", boardQuery, (draft) => {
              Object.assign(draft, mapBoardToViewModel(localBoard))
            })
          )
          try {
            const response = await queryFulfilled
            const serverBoard = response.data

            // Only update the cache if this is the last move request for the board
            if (isLastUpdateRequest(boardId, requestId)) {
              dispatch(
                boardApiV2.util.updateQueryData("getBoardV2", boardQuery, (draft) => {
                  Object.assign(draft, serverBoard)
                })
              )
            }
          } catch (error) {
            patchResult.undo()
          }
        }
      },
    }),
    updateColumn: builder.mutation<BoardViewModelV2, BoardColumnUpdateData>({
      query: ({ boardId, columnId, ...updateData }) => ({
        url: apiEndpoints.boardColumnItem(columnId, boardId),
        method: "PUT",
        body: updateData,
      }),
      transformResponse: (response: BoardResponseV2) => mapBoardResponseToViewModel(response),
      onQueryStarted: async ({ boardId, ...updateData }, { getState, dispatch, queryFulfilled, requestId }) => {
        lastUpdateRequestById[boardId] = requestId
        const state = getState()
        const localBoardData = selectBoardV2ById(state, boardId)

        if (localBoardData) {
          const boardQuery = { id: boardId, type: localBoardData.type }
          const localBoard = new Board(localBoardData)
          localBoard.updateColumn(updateData.columnId, { isVisible: updateData.isVisible })
          const patchResult = dispatch(
            boardApiV2.util.updateQueryData("getBoardV2", boardQuery, (draft) => {
              Object.assign(draft, mapBoardToViewModel(localBoard))
            })
          )
          try {
            const response = await queryFulfilled
            const serverBoard = response.data

            // Only update the cache if this is the last move request for the board
            if (isLastUpdateRequest(boardId, requestId)) {
              dispatch(
                boardApiV2.util.updateQueryData("getBoardV2", boardQuery, (draft) => {
                  Object.assign(draft, serverBoard)
                })
              )
            }
          } catch (error) {
            patchResult.undo()
          }
        }
      },
    }),
    updateColumnName: builder.mutation<BoardViewModelV2, BoardColumnNameUpdateData>({
      query: ({ boardId, columnId, ...updateData }) => ({
        url: apiEndpoints.columnName(columnId, boardId),
        method: "PUT",
        body: updateData,
      }),
      transformResponse: (response: BoardResponseV2) => mapBoardResponseToViewModel(response),
      onQueryStarted: async ({ boardId, ...updateData }, { getState, dispatch, queryFulfilled, requestId }) => {
        lastUpdateRequestById[boardId] = requestId
        const state = getState()
        const localBoardData = selectBoardV2ById(state, boardId)

        if (localBoardData) {
          const boardQuery = { id: boardId, type: localBoardData.type }
          const localBoard = new Board(localBoardData)
          localBoard.updateColumn(updateData.columnId, { name: updateData.name })
          const patchResult = dispatch(
            boardApiV2.util.updateQueryData("getBoardV2", boardQuery, (draft) => {
              Object.assign(draft, mapBoardToViewModel(localBoard))
            })
          )
          try {
            const response = await queryFulfilled
            const serverBoard = response.data

            // Only update the cache if this is the last move request for the board
            if (isLastUpdateRequest(boardId, requestId)) {
              dispatch(
                boardApiV2.util.updateQueryData("getBoardV2", boardQuery, (draft) => {
                  Object.assign(draft, serverBoard)
                })
              )
            }
          } catch (error) {
            patchResult.undo()
          }
        }
      },
    }),
  }),
})

export const {
  useAddTaskCardMutation,
  useAddTodoCardMutation,
  useMoveTodoCardMutation,
  useMoveTaskCardMutation,
  useUpdateColumnMutation,
  useUpdateColumnNameMutation,
} = boardApi

export type MoveCardData = {
  boardId: string
  cardId: string
  columnId: string
  destColumnId: string
  position: number
  updateSubtasks?: boolean
}

export type BoardColumnUpdateData = {
  boardId: string
  columnId: string
  isVisible: boolean
}

export type NewCardData = {
  boardId: string
  columnId: string
  title: string
}

export type BoardColumnNameUpdateData = {
  boardId: string
  columnId: string
  name: string
}
